import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Single axios instance. We rely on httpOnly cookies (set by the backend on
 * /api/auth/login) for authentication — no tokens are persisted in
 * localStorage to avoid XSS exfiltration. `withCredentials: true` makes the
 * browser include the auth cookies on every request.
 */
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// -------- Sudo (PIN) session bridge --------
// The PinProvider registers a handler here so any request that hits a
// PIN-protected endpoint can transparently prompt the user, then retry.
let sudoToken = null;
let sudoExpiresAt = 0;
let pinPromptHandler = null; // (operation) => Promise<string|null>
let pinSetupHandler = null;  // () => Promise<boolean>

export function setSudoToken(token, expiresAtIso) {
  sudoToken = token || null;
  sudoExpiresAt = expiresAtIso ? new Date(expiresAtIso).getTime() : 0;
}

export function clearSudoToken() {
  sudoToken = null;
  sudoExpiresAt = 0;
}

export function getSudoToken() {
  if (!sudoToken) return null;
  if (Date.now() >= sudoExpiresAt) {
    sudoToken = null;
    sudoExpiresAt = 0;
    return null;
  }
  return sudoToken;
}

export function registerPinPromptHandler(fn) { pinPromptHandler = fn; }
export function registerPinSetupHandler(fn) { pinSetupHandler = fn; }

api.interceptors.request.use((config) => {
  const t = getSudoToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers["X-Sudo-Token"] = t;
  }
  return config;
});

// Public route prefixes that must NOT trigger a redirect to /login on 401.
// Add new public pages here to avoid the interceptor bouncing anonymous users.
const PUBLIC_PATHS = ["/login", "/confirm", "/kai/", "/firmar/", "/firmar-dgt/", "/horarios/", "/alta/", "/documentos/", "/psicotecnico/"];

api.interceptors.response.use(
  (r) => {
    // V1.87 · Cualquier escritura correcta avisa a la app para que las
    // pantallas abiertas (calendario, mesa, fichas) recarguen al momento.
    const method = String(r?.config?.method || "").toLowerCase();
    if (method && method !== "get" && method !== "head") {
      window.dispatchEvent(new CustomEvent("app-data-changed", {
        detail: { url: r?.config?.url || "", method },
      }));
    }
    return r;
  },
  async (err) => {
    const response = err?.response;
    const config = err?.config;
    if (response?.status === 401) {
      const path = window.location.pathname;
      if (!PUBLIC_PATHS.some((p) => path.startsWith(p))) {
        window.location.href = "/login";
      }
      return Promise.reject(err);
    }
    // PIN maestro no configurado — abrir wizard y reintentar
    if (response?.status === 428 && !config?.__pin_setup_retried && pinSetupHandler) {
      const done = await pinSetupHandler();
      if (done && config) {
        config.__pin_setup_retried = true;
        return api(config);
      }
    }
    // Sesión sudo requerida — abrir dialog y reintentar tras verificar PIN
    if (response?.status === 440 && !config?.__pin_retried && pinPromptHandler) {
      const op =
        response.headers?.["x-protected-operation"] ||
        response.headers?.["X-Protected-Operation"] ||
        "";
      const newToken = await pinPromptHandler(op);
      if (newToken && config) {
        config.__pin_retried = true;
        config.headers = config.headers || {};
        config.headers["X-Sudo-Token"] = newToken;
        return api(config);
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Ha ocurrido un error.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
